import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    computed,
    effect,
    inject,
    input,
} from '@angular/core';
import { textStyleClass, TextStyleCategory, TextStyleSize } from '@sirius/typography';

import { HeadingSize } from '../_shared/types/heading/heading-size.type';
import { HeadingType } from '../_shared/types/heading/heading.type';
import { HeadingService } from './heading.service';

/**
 * Subset of `TextStyleCategory` that `sirius-heading` actually supports.
 */
export type HeadingCategory = Extract<TextStyleCategory, 'title' | 'display'>;

/**
 * Heading component aligned with the typography API in `@sirius/typography`.
 *
 * Use the `textStyle*`-prefixed inputs (matches `TextStyleComponent`):
 *
 * - `textStyleCategory` — `'title' | 'display'` (default `'title'`)
 * - `textStyleSize` — `'xs' | 's' | 'm' | 'l' | 'xl'` (default `'l'`)
 *
 * The legacy `type` and `size` inputs still work as deprecated aliases that
 * emit a runtime console warning when used.
 *
 * @example
 * ```html
 * <sirius-heading text="Page title" textStyleCategory="title" textStyleSize="xl" [h]="1" />
 * ```
 */
@Component({
    selector: 'sirius-heading',
    templateUrl: './heading.component.html',
    styleUrls: ['./heading.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.is-title]': 'resolvedCategory() === "title"',
        '[class.is-display]': 'resolvedCategory() === "display"',
        '[class.is-xxsmall]': 'resolvedRawSize() === "xxs"',
        '[class.is-xsmall]': 'resolvedRawSize() === "xs"',
        '[class.is-small]': 'resolvedRawSize() === "s"',
        '[class.is-medium]': 'resolvedRawSize() === "m"',
        '[class.is-large]': 'resolvedRawSize() === "l"',
        '[class.is-xlarge]': 'resolvedRawSize() === "xl"',
    },
})
export class HeadingComponent implements OnInit, OnDestroy {
    private _headingService = inject(HeadingService);

    /**
     * Typography category. Mirrors `TextStyleComponent`'s `siriusTextStyle`
     * but constrained to the categories meaningful for headings.
     */
    readonly textStyleCategory = input<HeadingCategory>('title');

    /**
     * Typography size. Mirrors `TextStyleComponent`'s `textStyleSize`.
     * Display category clamps to `s | m | l` internally per the typography spec.
     */
    readonly textStyleSize = input<TextStyleSize>('l');

    readonly h = input<1 | 2 | 3 | 4 | 5 | 6 | undefined>();
    readonly itemprop = input<string | undefined>();

    /** @deprecated Use `textStyleCategory` instead. Will be removed in a future major version. */
    readonly type = input<HeadingType | undefined>(undefined);

    /** @deprecated Use `textStyleSize` instead. Will be removed in a future major version. `'xxs'` collapses to `'xs'` for typography purposes. */
    readonly size = input<HeadingSize | undefined>(undefined);

    count = 0;
    readonly text = input('', {
        transform: (value: string | null | undefined) =>
            String(value ?? '')
                .replace(new RegExp('---', 'g'), '\u000A')
                .replace(new RegExp('--', 'g'), '\u00AD'),
    });

    private readonly deprecationWarnings = new Set<string>();

    constructor() {
        effect(() => {
            if (this.type() !== undefined) this.warnDeprecated('type', 'textStyleCategory');
            if (this.size() !== undefined) this.warnDeprecated('size', 'textStyleSize');
        });
    }

    /**
     * The unmapped category (legacy `type` wins if explicitly set).
     */
    readonly resolvedCategory = computed<HeadingCategory>(() => this.type() ?? this.textStyleCategory());

    /**
     * The unmapped size in the consumer's vocabulary (legacy `size` wins if
     * explicitly set, so `'xxs'` is preserved here for the host class).
     */
    readonly resolvedRawSize = computed<HeadingSize | TextStyleSize>(() => this.size() ?? this.textStyleSize());

    /**
     * The actual category passed to `siriusTextStyle`.
     */
    readonly textCategory = computed<TextStyleCategory>(() => this.resolvedCategory());

    /**
     * The actual size passed to `textStyleSize` on the typography directive.
     * - `xxs` is collapsed to `xs` (the typography system has no `xxs`).
     * - `display` clamps to `s | m | l` since that's all the spec allows.
     */
    readonly textSize = computed<TextStyleSize>(() => {
        const raw = this.resolvedRawSize();
        const normalized: TextStyleSize = raw === 'xxs' ? 'xs' : raw;

        if (this.resolvedCategory() === 'display') {
            if (normalized === 'xl' || normalized === 'l') return 'l';
            if (normalized === 'm') return 'm';
            return 's';
        }

        return normalized;
    });

    /**
     * Typography utility class applied to the heading element (ADR 0001).
     * Replaces the legacy `[siriusTextStyle]` directive with a zero-runtime
     * static class string.
     */
    readonly textClass = computed<string>(() => textStyleClass(this.textCategory(), this.textSize()));

    get headingTag(): 1 | 2 | 3 | 4 | 5 | 6 {
        if (this.h()) {
            return this.h() as 1 | 2 | 3 | 4 | 5 | 6;
        }

        return this.count === 0 ? 1 : 2;
    }

    ngOnInit(): void {
        this.count = this._headingService.count;
        this._headingService.incrementCounter();
    }

    ngOnDestroy(): void {
        if (this.count === 0) {
            this._headingService.resetCounter();
        } else {
            this._headingService.decrementCounter();
        }
    }

    private warnDeprecated(oldName: string, newName: string): void {
        if (this.deprecationWarnings.has(oldName)) return;
        this.deprecationWarnings.add(oldName);
        console.warn(`[HeadingComponent] "${oldName}" is deprecated. Use "${newName}" instead.`);
    }
}
